import React from "react";
import {IDrawer, Text, Stack, Flex} from "@chakra-ui/core";
import ArrowLeftIcon from "@pency/components/icons/ArrowLeft";
import ShareIcon from "@pency/components/icons/Share";
import FormControl from "@pency/components/form/FormControl";
import Button from "@pency/components/controls/Button";
import FormLabel from "@pency/components/form/FormLabel";
import Stepper from "@pency/components/inputs/Stepper";
import Textarea from "@pency/components/inputs/Textarea";
import Drawer, {DrawerBody, DrawerFooter} from "@pency/components/structure/Drawer";

import SummaryButton from "../SummaryButton";

import {Product} from "~/product/types";
import TruncatedText from "~/ui/feedback/ToggleableText";
import {useTranslation} from "~/i18n/hooks";
import {useToast} from "~/hooks/toast";
import {useAnalytics} from "~/analytics/hooks";
import Carousel from "~/ui/feedback/Carousel";

interface Props extends Omit<IDrawer, "children"> {
  onSubmit: (product: Product, count: number, note: string) => void;
  product: Product;
}

const CartItemDrawer: React.FC<Props> = ({onClose, product, onSubmit, ...props}) => {
  const [count, setCount] = React.useState(1);
  const [note, setNote] = React.useState("");
  const t = useTranslation();
  const log = useAnalytics();
  const toast = useToast();
  const canShare = {
    prompt: Boolean(navigator?.share),
    clipboard: Boolean(navigator?.clipboard),
  };

  function handleSubmit() {
    onSubmit(product, count, note);
  }

  function handleShare() {
    if (canShare.prompt) {
      navigator
        .share({
          title: product.title,
          text: product.description,
          url: window.location.href,
        })
        .then(() => {
          toast({
            status: t("cartItemDrawer.share.prompt.status"),
            title: t("cartItemDrawer.share.prompt.title"),
            description: t("cartItemDrawer.share.prompt.description"),
          });

          log.share(product, "mobile");
        })
        .catch(() => {
          console.info("El dialogo de share fue cerrado");
        });
    } else if (canShare.clipboard) {
      navigator.clipboard
        .writeText(window.location.href)
        .then(() => {
          toast({
            status: t("cartItemDrawer.share.clipboard.status"),
            title: t("cartItemDrawer.share.clipboard.title"),
            description: t("cartItemDrawer.share.clipboard.description"),
          });

          log.share(product, "desktop");
        })
        .catch(() => {
          toast({
            status: t("cartItemDrawer.share.clipboard.error.status"),
            title: t("cartItemDrawer.share.clipboard.error.title"),
            description: t("cartItemDrawer.share.clipboard.error.description"),
          });
        });
    }
  }

  function handleNoteChange(event: React.ChangeEvent<HTMLInputElement>) {
    setNote(event.target.value);
  }

  React.useLayoutEffect(() => {
    if (product) {
      log.viewProduct(product);
    }
  }, [product, log]);

  // If we get here by any point, return null
  if (product.type === "hidden") return null;

  const items = [
    {
      id: "temp",
      note: "",
      product,
      count,
    },
  ];

  return (
    <Drawer id="cart-item" placement="right" size="md" onClose={onClose} {...props}>
      <DrawerBody paddingX={0}>
        <ArrowLeftIcon
          background="white"
          boxShadow="md"
          cursor="pointer"
          left={0}
          marginTop={4}
          paddingX={4}
          paddingY={3}
          position="absolute"
          roundedRight="lg"
          top={0}
          zIndex={1}
          onClick={onClose}
        />
        {(canShare.clipboard || canShare.prompt) && (
          <ShareIcon
            background="white"
            boxShadow="md"
            cursor="pointer"
            marginTop={4}
            paddingX={4}
            paddingY={3}
            position="absolute"
            right={0}
            roundedLeft="lg"
            top={0}
            zIndex={1}
            onClick={handleShare}
          />
        )}
        {Boolean(product.images?.length) && (
          <Carousel baseHeight="30vh" maxHeight={500} srcSet={product.images} />
        )}
        <Stack
          shouldWrapChildren
          direction="column"
          flex={1}
          marginTop={product.images?.length ? 0 : 16}
          paddingTop={4}
          paddingX={{base: 4, sm: 12}}
          spacing={6}
        >
          <Stack spacing={2}>
            <Text fontSize="2xl" fontWeight="bold" lineHeight="normal" overflowWrap="break-word">
              {product.title}
            </Text>
            {product.description && (
              <TruncatedText color="gray.500" fontSize="md" limit={280} whiteSpace="pre-line">
                {product.description}
              </TruncatedText>
            )}
          </Stack>
          <Flex alignItems="center" justifyContent="space-between">
            <FormLabel padding={0}>{t("common.count")}</FormLabel>
            <Stepper min={1} value={count} onChange={setCount} />
          </Flex>
          <FormControl label={t("cartItemDrawer.comments.label")}>
            <Textarea
              max={140}
              placeholder={t("cartItemDrawer.comments.placeholder")}
              value={note}
              onChange={handleNoteChange}
            />
          </FormControl>
        </Stack>
      </DrawerBody>
      <DrawerFooter>
        {["unavailable", "available", "promotional"].includes(product.type) && (
          <SummaryButton
            isDisabled={product.type === "unavailable"}
            items={items}
            onClick={(event) => {
              event.stopPropagation();

              handleSubmit();
            }}
          >
            {t("common.add")}
          </SummaryButton>
        )}
        {product.type === "ask" && (
          <Button
            boxShadow="lg"
            size="lg"
            variantColor="primary"
            width="100%"
            onClick={(event) => {
              event.stopPropagation();

              handleSubmit();
            }}
          >
            {t("common.add")}
          </Button>
        )}
      </DrawerFooter>
    </Drawer>
  );
};

export default CartItemDrawer;
